// Fijados — the channel's noticeboard (PinnedMessagesSheet in ChannelInfoSheet.swift, rows from
// PinnedPostsPresentation). The store paints first; the open fetches `GET /channels/{id}/pinned`
// and RECONCILES (an unpin elsewhere leaves no trace in that answer). A row jumps to the message
// itself — a pinned reply to the reply; its trailing button unpins in place.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { PinnedPostsPresentation } from '../../../core/pinnedPostsPresentation';
import type { ChannelRecord } from '../../../core/records';
import { TeammateNameDisplay } from '../../../core/teammateNameDisplay';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { useLive, useMinuteClock } from '../../hooks';
import { EmptyState, InlineStatus } from '../../kit/Kit';
import { PostResultRow } from '../../kit/PostResultRow';
import { useShell } from '../../shell/ShellContext';
import { usersById } from './panelModel';

export function PinnedTab({ channel, name }: { channel: ChannelRecord; name: string }) {
  const shell = useShell();
  const now = useMinuteClock();
  const { value: board } = useLive(() => data.call('pinnedPosts', channel.id), ['posts', 'users'], [channel.id]);
  const [status, setStatus] = useState<'loading' | 'idle' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    data.call('refreshPinnedPosts', channel.id).then(
      (ok) => alive && setStatus(ok ? 'idle' : 'failed'),
      () => alive && setStatus('failed'),
    );
    return () => {
      alive = false;
    };
  }, [channel.id, attempt]);

  const users = useMemo(() => usersById(board?.users ?? []), [board]);
  const rows = useMemo(
    () =>
      board
        ? PinnedPostsPresentation.rows({
            posts: board.posts,
            usersById: users,
            nameFormat: TeammateNameDisplay.current(),
            now,
            yesterdayLabel: t('channelList.time.yesterday'),
            attachmentPlaceholder: t('channelList.preview.attachment'),
            unknownAuthorLabel: t('channel.reaction.unknown'),
          })
        : [],
    [board, users, now],
  );

  const unpin = (postId: string) => {
    data.call('setPostPinned', postId, false).then(
      (ok) => !ok && shell.toast(t('channel.action.failed'), 'error'),
      () => shell.toast(t('channel.action.failed'), 'error'),
    );
  };

  const failed = status === 'failed' ? <InlineStatus kind="error" text={t('web.channelPanel.pinnedFailed')} onRetry={() => setAttempt((n) => n + 1)} /> : null;
  if (!rows.length) {
    return (
      <>
        {failed}
        {status === 'loading' ? <InlineStatus kind="loading" text={t('channel.loading.accessibility')} /> : <EmptyState title={t('channel.pinned.empty')} />}
      </>
    );
  }
  return (
    <>
      {failed}
      <div className="cp-results">
        {rows.map((row) => (
          <PostResultRow
            key={row.postId}
            userId={row.userId}
            authorName={row.authorName}
            authorVersion={users.get(row.userId)?.lastPictureUpdate ?? 0}
            place={name}
            placeIsPrivate={channel.type === 'P'}
            time={row.timeLabel}
            snippet={row.preview}
            onOpen={() => shell.jumpToPost(row.postId)}
            trailing={
              <button className="icon-button cp-unpin" title={t('channel.unpin')} aria-label={t('channel.unpin')} onClick={() => unpin(row.postId)}>
                <Icon name="pin" size={16} />
              </button>
            }
          />
        ))}
      </div>
    </>
  );
}
